import React, { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import {
  EnterpriseDetail,
  EnterpriseSummary,
  RelatedEnterprise,
} from '../../clients/kyousou_api_client'
import mainBgImage from '../../images/main_bg.png'
import RadioButtonGroup from '../../components/buttons/radio_button_group'
import { useEnterpriseDetail } from '../../hooks/use_enterprise_detail'
import EnterprisesDetailTab from './tabs/enterprises_detail_tab'
import SdgsTab from './tabs/sdgs_tab'
import CoCreationTab from './tabs/co_creation_tab'
import { Kuchikomi, Enterprise } from '../../clients/kuchikomi_api_client'

import type { RadioButtonType } from '../../components/buttons/radio_button_group'
import CompanyInfoSection from '../../components/common/company_info_section'
import RelatedEnterprisesSection from '../../components/common/related_enterprises_section'
import SendMatchingModal from '../../components/modals/send_matching_modal'
import {
  handleError,
  ApiErrorResponse,
  getKyousouApiClient,
} from '../../services/network'
import { useSnackBar } from '../../providers/snack_bar_provider'
import FlatButton from '../../components/buttons/flat_button'
import { useEnterpriseMatchingStatus } from '../../hooks/use_enterprise_matching_status'
import { MatchingRequestSection } from '../../components/sections/matching_request_section'
import { useVisitorTracking } from '../../hooks/use_visitor_tracking'
import FavoriteButton from '../../components/buttons/favorite_button'
import { useEnterpriseFavorite } from '../../hooks/use_enterprise_favorite'
import MatchingRateBadge from '../../components/badges/matching_rate_badge'
import LoadingSpinner from '../../components/loading/loading_spinner'
import Breadcrumb from '../../components/common/breadcrumb'

const getEstablishYearText = (
  establishDate: string | null | undefined,
  suffix = '年'
): string => {
  if (!establishDate) return ''
  return `${establishDate.split('-')[0]}${suffix}`
}

// 定数
const SIDEBAR_ITEM_CLASSES = 'flex gap-4'
const SIDEBAR_LABEL_CLASSES =
  'font-medium text-main-darkgray w-20 flex-shrink-0'
const SIDEBAR_VALUE_CLASSES = 'text-main-black flex-1'

const EnterprisesDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!id) {
    navigate('/404')
  }

  // 訪問記録送信
  useVisitorTracking({ enterpriseId: id })

  const apiClient = getKyousouApiClient()
  const {
    status: matchingStatus,
    loading: matchingLoading,
    confirm: matchingConfirm,
  } = useEnterpriseMatchingStatus(id)
  const {
    enterprise,
    enterpriseSummary,
    relatedEnterprises,
    kuchikomiEnterprise,
    kuchikomis,
    loading,
    error,
  }: {
    enterprise: EnterpriseDetail | null
    enterpriseSummary: EnterpriseSummary | null
    setEnterprise: (enterprise: EnterpriseDetail | null) => void
    relatedEnterprises: RelatedEnterprise[]
    kuchikomiEnterprise: Enterprise | null
    kuchikomis: Kuchikomi[]
    loading: boolean
    error: string | null
  } = useEnterpriseDetail(id)

  const [selectedTab, setSelectedTab] = useState<
    'overview' | 'sdgs' | 'co-creation'
  >('overview')
  const [displayedTab, setDisplayedTab] = useState<
    'overview' | 'sdgs' | 'co-creation'
  >('overview')

  const { isFavorited, toggleFavorite } = useEnterpriseFavorite(
    id,
    enterpriseSummary?.isFavorited
  )

  // 応募ボタンのクリックハンドラ
  const handleApplicationClick = () => {
    if (!enterprise?.isOwn && matchingStatus === 'none') {
      setIsModalOpen(true)
    }
  }

  // 応募送信処理
  const handleApplicationSubmit = async (message: string) => {
    if (!id) return

    setIsSubmitting(true)
    try {
      await apiClient.enterprise.matchingRequestCreate(id, { message })
      showSnackBar(['マッチリクエストを送信しました'], 'success')
      setIsModalOpen(false)

      // 最新の状態を取得
      matchingConfirm()
    } catch (error) {
      handleError(error as ApiErrorResponse, showSnackBar)
    } finally {
      setIsSubmitting(false)
    }
  }

  // チャット画面へ遷移
  const handleChatClick = () => {
    navigate('/chat')
  }

  // データ存在チェック関数
  const hasOverviewData = useMemo(
    () =>
      (enterprise?.summary &&
        (enterprise.summary.companyProfile ||
          enterprise.summary.companyFeature ||
          enterprise.summary.providedResource ||
          enterprise.summary.majorCustomer ||
          enterprise.summary.shareholder ||
          (enterprise.summary.openInnovationAchievements?.achievements &&
            enterprise.summary.openInnovationAchievements.achievements.length >
              0))) ||
      enterprise?.companyTypes ||
      enterprise?.companyStage ||
      enterprise?.servicePurposes,
    [enterprise]
  )

  const hasSDGsData = useMemo(
    () =>
      enterprise?.sdgsInitiatives &&
      (enterprise.sdgsInitiatives.renewableEnergy ||
        enterprise.sdgsInitiatives.energyEfficiency ||
        enterprise.sdgsInitiatives.environmentalEducation ||
        enterprise.sdgsInitiatives.sdgsInfo),
    [enterprise]
  )

  const hasCoCreationData = useMemo(
    () =>
      enterprise?.coCreationInfo &&
      (enterprise.coCreationInfo.title ||
        enterprise.coCreationInfo.content ||
        enterprise.coCreationInfo.issue ||
        enterprise.coCreationInfo.goal ||
        enterprise.coCreationInfo.requirement),
    [enterprise]
  )

  // タブボタンの定義
  const tabButtons: RadioButtonType[] = useMemo(() => {
    const buttons: RadioButtonType[] = []

    // 企業概要タブはSDGsタブか共創希望タブがある場合のみ表示（企業概要タブが単独の場合は表示しない）
    if (hasSDGsData || hasCoCreationData) {
      buttons.push({
        label: '企業概要',
        onClick: () => setSelectedTab('overview'),
      })
    }

    if (hasSDGsData) {
      buttons.push({
        label: 'SDGsの取り組み',
        onClick: () => setSelectedTab('sdgs'),
      })
    }

    if (hasCoCreationData) {
      buttons.push({
        label: '共創希望',
        onClick: () => setSelectedTab('co-creation'),
      })
    }

    return buttons
  }, [hasSDGsData, hasCoCreationData, setSelectedTab])

  // アクティブなタブのインデックスを計算
  const activeTabIndex = useMemo(() => {
    return tabButtons.findIndex((button) => {
      if (displayedTab === 'overview' && button.label === '企業概要')
        return true
      if (displayedTab === 'sdgs' && button.label === 'SDGsの取り組み')
        return true
      if (displayedTab === 'co-creation' && button.label === '共創希望')
        return true
      return false
    })
  }, [tabButtons, displayedTab])

  useEffect(() => {
    // データがあるタブのみ表示可能
    const availableTabs = []
    if (hasOverviewData) availableTabs.push('overview')
    if (hasSDGsData) availableTabs.push('sdgs')
    if (hasCoCreationData) availableTabs.push('co-creation')

    // 選択中のタブが利用可能な場合はそのまま表示
    if (availableTabs.includes(selectedTab)) {
      setDisplayedTab(selectedTab)
    } else if (availableTabs.length > 0) {
      // 利用可能なタブがある場合、最初のタブを選択・表示
      setSelectedTab(availableTabs[0] as 'overview' | 'sdgs' | 'co-creation')
      setDisplayedTab(availableTabs[0] as 'overview' | 'sdgs' | 'co-creation')
    }
  }, [selectedTab, hasOverviewData, hasSDGsData, hasCoCreationData])

  useEffect(() => {
    window.scrollTo(0, 0)
    setSelectedTab('overview')
    setDisplayedTab('overview')
  }, [id])

  const isLoading = loading || matchingLoading
  if (isLoading) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8 bg-main-white">
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  if (error || !enterprise) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8 bg-main-white">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-main-darkgray mb-4">
              企業が見つかりません
            </h1>
            <p className="text-main-darkgray">
              {error || '指定された企業情報を取得できませんでした。'}
            </p>
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <Breadcrumb
        items={[
          { label: 'ホーム', path: '/dashboard' },
          { label: '企業詳細検索', path: '/enterprises/search' },
          { label: '企業詳細' },
        ]}
      />

      {/* メイン画像セクション */}
      <div className="overflow-hidden">
        <img
          src={enterprise.summary?.bannerImageUrl || mainBgImage}
          alt="企業メイン画像"
          className="w-full aspect-[4/1] object-cover"
        />
      </div>
      <div className="container mx-auto px-4 py-8 bg-main-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* メインコンテンツ */}
          <div className="lg:col-span-2">
            {/* ヘッダーセクション */}
            <div className="mb-6">
              {/* 企業情報セクション */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                {/* 企業アイコン */}
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-20 h-20 bg-main-lightgray rounded flex items-center justify-center flex-shrink-0">
                      {kuchikomiEnterprise?.logoUrl ? (
                        <img
                          src={kuchikomiEnterprise.logoUrl}
                          alt="企業ロゴ"
                          className="w-full h-full object-cover rounded"
                        />
                      ) : (
                        <span className="material-icons !text-3xl text-main-darkgray">
                          landscape
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-2xl">
                        {kuchikomiEnterprise?.name}
                      </div>
                      {/* TODO:高田 nameEnを入力してもらうところと保存場所を考える*/}
                      {/* <div className="text-xs text-main-darkgray mb-2">
                        {enterprise.nameEn}
                      </div> */}
                      {/* 業種、住所、企業規模、設立年等の情報 */}
                      <div className="text-sm text-main-darkgray flex items-center gap-x-4 gap-y-1 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="material-icons text-main-black text-sm">
                            business
                          </span>
                          <span>
                            {kuchikomiEnterprise?.industryParentCode?.value}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="material-icons text-main-black text-sm">
                            location_on
                          </span>
                          <span>{kuchikomiEnterprise?.address}</span>
                        </div>
                        {/* TODO:高田 employeeCountを入力してもらうところと保存場所を考える*/}
                        {/* <div className="flex items-center gap-2">
                          <span className="material-icons text-main-black text-sm">
                            group
                          </span>
                          <span>{enterprise.employeeCount}</span>
                        </div> */}
                        <div className="flex items-center gap-2">
                          <span className="material-icons text-main-black text-sm">
                            calendar_today
                          </span>
                          <span>
                            {getEstablishYearText(
                              kuchikomiEnterprise?.establishDate,
                              '年設立'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* お気に入りボタンとマッチング率 */}
                {!enterprise.isOwn && (
                  <div className="mt-4 lg:mt-0 lg:ml-4 flex flex-col items-end gap-4">
                    <FavoriteButton
                      isFavorited={isFavorited}
                      onClick={toggleFavorite}
                      isOwn={enterprise.isOwn}
                    />
                    <MatchingRateBadge
                      matchingRate={enterpriseSummary?.matchingRate ?? 0}
                      isOwn={enterprise.isOwn}
                    />
                  </div>
                )}
              </div>
              {/* SDGsカテゴリー */}
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {kuchikomiEnterprise?.sdgsCategory &&
                    // 数字が小さい順にソート
                    kuchikomiEnterprise.sdgsCategory
                      .sort((a, b) => Number(a.code) - Number(b.code))
                      .map((category) => (
                        <span
                          key={category.code}
                          className="bg-main-lightgreen text-main-green border border-main-green px-3 py-1 rounded-full text-xs font-bold"
                        >
                          目標 {category.code}: {category.value}
                        </span>
                      ))}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-start mb-6">
              {/* タブナビゲーション */}
              <div className="flex-1">
                {tabButtons.length > 0 && (
                  <div className="inline-flex">
                    <RadioButtonGroup
                      buttons={tabButtons}
                      activeIndex={activeTabIndex}
                    />
                  </div>
                )}
              </div>

              {enterprise.isOwn && (
                <div className="flex gap-2">
                  <FlatButton onClick={() => navigate(`/enterprises/edit`)}>
                    編集する
                  </FlatButton>
                </div>
              )}
            </div>

            {/* タブコンテンツ */}
            {kuchikomiEnterprise && (
              <div className="border border-main-lightgray rounded p-6">
                {displayedTab === 'overview' && (
                  <EnterprisesDetailTab
                    enterprise={enterprise}
                    kuchikomiEnterprise={kuchikomiEnterprise}
                    kuchikomis={kuchikomis}
                  />
                )}

                {displayedTab === 'sdgs' && hasSDGsData && (
                  <SdgsTab enterprise={enterprise} />
                )}

                {displayedTab === 'co-creation' && hasCoCreationData && (
                  <CoCreationTab enterprise={enterprise} />
                )}
              </div>
            )}
          </div>

          {/* サイドバー */}
          {
            <div className="lg:col-span-1">
              <div className="p-6 top-4">
                {/* アクション欄 */}
                {!enterprise.isOwn && (
                  <MatchingRequestSection
                    matchingStatus={matchingStatus}
                    isSubmitting={isSubmitting}
                    onClick={handleApplicationClick}
                    onChatClick={handleChatClick}
                    isCheckingStatus={matchingLoading}
                  />
                )}

                {/* 会社情報 */}
                {enterpriseSummary && (
                  <CompanyInfoSection enterprise={enterpriseSummary} />
                )}

                {/* 本掲載の担当者 - データがある場合のみ表示 */}
                {enterprise.publicationResponsibility &&
                  (enterprise.publicationResponsibility.departmentName ||
                    enterprise.publicationResponsibility.responsiblePerson
                      ?.lastName ||
                    enterprise.publicationResponsibility.responsiblePerson
                      ?.firstName ||
                    enterprise.publicationResponsibility.responsiblePerson
                      ?.position ||
                    enterprise.publicationResponsibility
                      .departmentDescription) && (
                    <div className="mb-8 border border-main-lightgray rounded p-4">
                      <div className="flex justify-between mb-2">
                        <h3 className="text-lg font-bold mb-4">
                          本掲載の担当者
                        </h3>{' '}
                        {enterprise.publicationResponsibility.responsiblePerson
                          ?.profileImg && (
                          <img
                            src={
                              enterprise.publicationResponsibility
                                .responsiblePerson?.profileImg
                            }
                            alt="担当者"
                            className="w-20 h-20 rounded-lg"
                          />
                        )}
                      </div>
                      <div className="space-y-4 text-sm">
                        {enterprise.publicationResponsibility
                          .departmentName && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>
                              部署名
                            </span>
                            <p className={SIDEBAR_VALUE_CLASSES}>
                              {
                                enterprise.publicationResponsibility
                                  .departmentName
                              }
                            </p>
                          </div>
                        )}
                        {enterprise.publicationResponsibility.responsiblePerson
                          ?.position && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>役職</span>
                            <p className={SIDEBAR_VALUE_CLASSES}>
                              {
                                enterprise.publicationResponsibility
                                  .responsiblePerson.position
                              }
                            </p>
                          </div>
                        )}
                        {(enterprise.publicationResponsibility.responsiblePerson
                          ?.lastName ||
                          enterprise.publicationResponsibility.responsiblePerson
                            ?.firstName) && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>
                              担当者
                            </span>
                            <p className={SIDEBAR_VALUE_CLASSES}>
                              {`${enterprise.publicationResponsibility.responsiblePerson?.lastName || ''} ${enterprise.publicationResponsibility.responsiblePerson?.firstName || ''}`.trim()}
                            </p>
                          </div>
                        )}
                        {enterprise.publicationResponsibility
                          .departmentDescription && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>
                              部署説明
                            </span>
                            <p
                              className={
                                SIDEBAR_VALUE_CLASSES +
                                ' text-sm leading-relaxed break-all'
                              }
                            >
                              {
                                enterprise.publicationResponsibility
                                  .departmentDescription
                              }
                            </p>
                          </div>
                        )}
                        {enterprise.publicationResponsibility.responsiblePerson
                          ?.introduction && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>
                              自己紹介
                            </span>
                            <p
                              className={
                                SIDEBAR_VALUE_CLASSES +
                                ' text-sm leading-relaxed break-all'
                              }
                            >
                              {
                                enterprise.publicationResponsibility
                                  .responsiblePerson.introduction
                              }
                            </p>
                          </div>
                        )}
                        {enterprise.publicationResponsibility.responsiblePerson
                          ?.mission && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>
                              ミッション
                            </span>
                            <p
                              className={
                                SIDEBAR_VALUE_CLASSES +
                                ' text-sm leading-relaxed break-all'
                              }
                            >
                              {
                                enterprise.publicationResponsibility
                                  .responsiblePerson.mission
                              }
                            </p>
                          </div>
                        )}
                        {enterprise.publicationResponsibility.responsiblePerson
                          ?.comment && (
                          <div className={SIDEBAR_ITEM_CLASSES}>
                            <span className={SIDEBAR_LABEL_CLASSES}>
                              コメント
                            </span>
                            <p
                              className={
                                SIDEBAR_VALUE_CLASSES +
                                ' text-sm leading-relaxed break-all'
                              }
                            >
                              {
                                enterprise.publicationResponsibility
                                  .responsiblePerson.comment
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* 関連企業 */}
                <RelatedEnterprisesSection
                  relatedEnterprises={relatedEnterprises}
                  onEnterpriseClick={(relatedEnterprise) => {
                    navigate(`/enterprises/detail/${relatedEnterprise.id}`)
                  }}
                />
              </div>
            </div>
          }
        </div>
      </div>

      {/* 応募モーダル */}
      {enterpriseSummary && (
        <SendMatchingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleApplicationSubmit}
          recruitmentTitle={`${enterpriseSummary.name}への共創提案`}
          enterpriseName={enterpriseSummary.name}
          isSubmitting={isSubmitting}
        />
      )}
    </AuthenticatedLayout>
  )
}

export default EnterprisesDetailPage
